/**
 * shopchooka-gallery-a11y.js
 * Upload to: Shopify Admin > Online Store > Themes > Edit code > Assets
 * Then add to layout/theme.liquid before </body>:
 *   {{ 'shopchooka-gallery-a11y.js' | asset_url | script_tag }}
 *
 * Fixes:
 *  Issue #1 — Keyboard-accessible product image gallery
 *             SC 2.1.1 (Keyboard), SC 4.1.2 (Name Role Value), SC 2.4.7 (Focus Visible)
 *  Issue #2 — Interactive elements inside aria-hidden containers
 *             SC 4.1.2 (Name Role Value)
 *             a) Flickity hidden slides: zoom buttons unreachable to keyboard
 *             b) PhotoSwipe dialog:      lightbox buttons reachable when dialog is closed
 *  Issue #3 — Missing H1 on inner page templates (About Us, FAQ, Accessibility, etc.)
 *             SC 2.4.6 (Headings and Labels), SC 1.3.1 (Info and Relationships)
 *  Issue #4 — Two H1s on the homepage (logo + hero banner heading)
 *             SC 1.3.1 (Info and Relationships), SC 2.4.6 (Headings and Labels)
 *  Issue #5 — SVG <title> text leaking into button/link accessible names sitewide
 *  Issue #9  — Insufficient color contrast on sale savings text
 *              SC 1.4.3 (Contrast Minimum)
 *              .product__price-savings: #ff4e4e on white = 3.25:1 (need 4.5:1)
 *  Issue #10 — No visible focus indicator on any interactive element sitewide
 *              SC 2.4.7 (Focus Visible)
 *              Theme CSS sets outline:none globally with no box-shadow fallback
 *  Issue #11 — No skip-to-main-content link
 *              SC 2.4.1 (Bypass Blocks)
 *  Issue #12 — Stray <img> elements with no src/alt and unlabelled no-JS
 *              variant <select> on product pages
 *              SC 1.1.1 (Non-text Content), SC 4.1.2 (Name Role Value)
 *  Issue #13 — Header logo link and logo-bar partner links have no accessible name
 *              SC 2.4.4 (Link Purpose), SC 4.1.2 (Name Role Value)
 *             SC 4.1.2 (Name Role Value), SC 2.4.4 (Link Purpose)
 *             e.g. "Open menu" announced as "icon-hamburger Site navigation"
 *  Issue #14 — Blog post "related articles" placeholder links have no accessible name
 *              SC 2.4.4 (Link Purpose in Context)
 *              article__grid-image links with href="#" contain only a placeholder SVG
 *              with no aria-label, making them meaningless to screen reader users.
 */
(function () {
  'use strict';

  var FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

  // Skip nav must be injected immediately (before DOMContentLoaded) so it is
  // the very first focusable element in the DOM when Tab is pressed.
  initSkipNav(); // Issue #11

  document.addEventListener('DOMContentLoaded', function () {
    initGalleryA11y();    // Issue #1
    initAriaHiddenFix();  // Issue #2
    initMissingH1Fix();   // Issue #3
    initHomepageH1Fix();  // Issue #4
    initIconTitleFix();   // Issue #5
    initContrastFix();    // Issue #9
    initFocusFix();       // Issue #10
    initProductPageFix(); // Issue #12
    initLogoLinkFix();    // Issue #13
    initBlogPlaceholderFix(); // Issue #14
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // ISSUE #1 — Accessible thumbnail tablist
  // ═══════════════════════════════════════════════════════════════════════════

  function initGalleryA11y() {
    var scrollers = document.querySelectorAll('.product__thumbs--scroller');
    if (!scrollers.length) return;
    scrollers.forEach(function (scroller) { setupScroller(scroller); });
  }

  function setupScroller(scroller) {
    scroller.setAttribute('role', 'tablist');
    scroller.setAttribute('aria-label', 'Product image thumbnails');

    var thumbLinks = scroller.querySelectorAll('[data-product-thumb]');
    var total      = thumbLinks.length;

    var firstImg    = thumbLinks[0] && thumbLinks[0].querySelector('img');
    var productName = (firstImg && firstImg.alt) ||
                      (document.querySelector('h1') && document.querySelector('h1').textContent.trim()) ||
                      'product image';

    thumbLinks.forEach(function (link, i) {
      link.setAttribute('role', 'tab');
      link.setAttribute('aria-label', 'Image ' + (i + 1) + ' of ' + total + ': ' + productName);

      var isActive = link.classList.contains('is-active');
      link.setAttribute('aria-selected', isActive ? 'true' : 'false');
      link.setAttribute('tabindex',      isActive ? '0'    : '-1');

      var img = link.querySelector('img');
      if (img) { img.setAttribute('alt', ''); img.setAttribute('aria-hidden', 'true'); }

      link.addEventListener('keydown', function (e) {
        var links = Array.from(scroller.querySelectorAll('[data-product-thumb]'));
        var idx   = links.indexOf(this);
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault(); var next = links[(idx + 1) % links.length]; next.focus(); next.click();
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault(); var prev = links[(idx - 1 + links.length) % links.length]; prev.focus(); prev.click();
        } else if (e.key === 'Home') {
          e.preventDefault(); links[0].focus(); links[0].click();
        } else if (e.key === 'End') {
          e.preventDefault(); links[links.length - 1].focus(); links[links.length - 1].click();
        }
      });
    });

    var thumbObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.type === 'attributes' && m.attributeName === 'class' && m.target.hasAttribute('data-product-thumb')) {
          var link   = m.target;
          var active = link.classList.contains('is-active');
          link.setAttribute('aria-selected', active ? 'true' : 'false');
          link.setAttribute('tabindex',      active ? '0'    : '-1');
          if (active) { announceImage(link); }
        }
      });
    });

    thumbLinks.forEach(function (link) {
      thumbObserver.observe(link, { attributes: true, attributeFilter: ['class'] });
    });

    if (!document.querySelector('.product__gallery-announce')) {
      var live = document.createElement('div');
      live.className = 'product__gallery-announce';
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('aria-atomic', 'true');
      live.style.cssText = 'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;';
      document.body.appendChild(live);
    }
  }

  function announceImage(activeLink) {
    var live = document.querySelector('.product__gallery-announce');
    if (!live) return;
    var label = activeLink.getAttribute('aria-label') || '';
    live.textContent = '';
    setTimeout(function () { live.textContent = 'Showing ' + label; }, 50);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ISSUE #2 — Remove hidden elements from tab order
  //
  // Problem: two containers use aria-hidden="true" but leave focusable children
  // reachable by keyboard, which is a direct contradiction.
  //
  //  a) .product-main-slide[aria-hidden="true"]
  //     Flickity sets aria-hidden on every off-screen slide. Each slide contains
  //     a "View full-size image" zoom button that has no tabindex="-1", so
  //     keyboard users can Tab into invisible slides (7 buttons affected).
  //
  //  b) .pswp[aria-hidden="true"]
  //     The PhotoSwipe lightbox is a role="dialog" with aria-hidden="true" when
  //     closed, but its 3 internal buttons have no tabindex="-1".
  //
  // Fix strategy: MutationObserver on each container. When aria-hidden="true"
  // is applied, set tabindex="-1" (and data-a11y-restore) on every focusable
  // child. When aria-hidden is removed, restore their original tabindex values.
  // ═══════════════════════════════════════════════════════════════════════════

  function initAriaHiddenFix() {
    // ── a) Flickity product slides ───────────────────────────────────────────
    var slideshow = document.querySelector('[data-product-photos]');
    if (slideshow) {
      // Apply to any slides already hidden at load time
      slideshow.querySelectorAll('.product-main-slide[aria-hidden="true"]').forEach(hideChildren);

      // Watch for Flickity toggling aria-hidden as the carousel moves
      new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          if (m.type === 'attributes' && m.attributeName === 'aria-hidden') {
            if (m.target.classList.contains('product-main-slide')) {
              if (m.target.getAttribute('aria-hidden') === 'true') {
                hideChildren(m.target);
              } else {
                restoreChildren(m.target);
              }
            }
          }
        });
      }).observe(slideshow, { attributes: true, attributeFilter: ['aria-hidden'], subtree: true });
    }

    // ── b) PhotoSwipe dialog ─────────────────────────────────────────────────
    var pswp = document.querySelector('.pswp');
    if (pswp) {
      // Apply immediately if closed at load time
      if (pswp.getAttribute('aria-hidden') === 'true') hideChildren(pswp);

      new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          if (m.type === 'attributes' && m.attributeName === 'aria-hidden') {
            if (m.target.getAttribute('aria-hidden') === 'true') {
              hideChildren(m.target);
            } else {
              restoreChildren(m.target);
            }
          }
        });
      }).observe(pswp, { attributes: true, attributeFilter: ['aria-hidden'] });
    }
  }

  /**
   * Set tabindex="-1" on every focusable descendant of `container`.
   * Saves the original tabindex value in data-a11y-restore so we can put it back.
   */
  function hideChildren(container) {
    container.querySelectorAll(FOCUSABLE).forEach(function (el) {
      // Don't double-process elements we already hid
      if (el.hasAttribute('data-a11y-restore')) return;
      var original = el.getAttribute('tabindex');
      el.setAttribute('data-a11y-restore', original !== null ? original : '');
      el.setAttribute('tabindex', '-1');
    });
  }

  /**
   * Restore each focusable descendant's original tabindex when its container
   * becomes visible again.
   */
  function restoreChildren(container) {
    container.querySelectorAll('[data-a11y-restore]').forEach(function (el) {
      var original = el.getAttribute('data-a11y-restore');
      if (original === '') {
        el.removeAttribute('tabindex');
      } else {
        el.setAttribute('tabindex', original);
      }
      el.removeAttribute('data-a11y-restore');
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ISSUE #4 — Homepage has two H1s: the logo and the hero banner heading
  //
  // Problem: The site-header logo is correctly an <h1> on the homepage
  // (brand name = top-level landmark). But hero banner sections also render
  // their heading as <h1 class="h1 hero__title">, giving the page two H1s.
  //
  // Fix strategy: On the homepage only (template-index body class), find every
  // <h1> that is NOT inside the site header and replace the element with <h2>,
  // preserving all attributes and inner content. The visual styling is unchanged
  // because the "h1" CSS class remains on the element.
  // ═══════════════════════════════════════════════════════════════════════════

  function initHomepageH1Fix() {
    // Only apply on the homepage
    if (!document.body.classList.contains('template-index')) return;

    document.querySelectorAll('h1').forEach(function (h1) {
      // Keep the logo H1 — it lives inside the header section
      if (h1.closest('header') ||
          h1.closest('[data-section-type="header"]') ||
          h1.closest('[id*="header"]')) { return; }

      // Replace this H1 with an H2, copying all attributes and children
      var h2 = document.createElement('h2');
      Array.from(h1.attributes).forEach(function (attr) {
        h2.setAttribute(attr.name, attr.value);
      });
      h2.innerHTML = h1.innerHTML;
      h1.parentNode.replaceChild(h2, h1);
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ISSUE #3 — Inject visually-hidden H1 on page templates that have none
  //
  // Problem: Inner pages (About Us, FAQ, Accessibility, etc.) use Shopify's
  // "page" template. The sections on these pages never render an <h1>, which
  // violates SC 2.4.6 (Headings and Labels) and SC 1.3.1 (Info and Relationships).
  //
  // Fix strategy: On template-page body class pages, check for an existing H1.
  // If none exists, parse the page name from the browser <title> tag
  // (format: "Chooka | Women's Shoes and Rain Boots | Page Name") and insert
  // a visually-hidden <h1> as the first child of <main>. This gives screen
  // readers a proper document landmark without changing the visual layout.
  // ═══════════════════════════════════════════════════════════════════════════

  function initMissingH1Fix() {
    // Only apply on Shopify "page" templates
    if (!document.body.classList.contains('template-page')) return;

    // If an H1 already exists anywhere on the page, nothing to do
    if (document.querySelector('h1')) return;

    // Parse the page name from the <title> tag.
    // Handles two formats Shopify themes use:
    //   Pipe-separated:  "Chooka | Women's Shoes and Rain Boots | Page Name"  → take last segment
    //   Dash-separated:  "Page Name – Chooka"                                  → take first segment
    var titleText = document.title || '';
    var pageName  = '';
    if (titleText.indexOf('|') !== -1) {
      // Pipe format — page name is the last segment
      var pipeParts = titleText.split('|');
      pageName = pipeParts[pipeParts.length - 1].trim();
    } else if (titleText.indexOf('–') !== -1 || titleText.indexOf('—') !== -1) {
      // En-dash or em-dash format — page name is the first segment
      pageName = titleText.split(/\s*[–—]\s*/)[0].trim();
    } else if (titleText.indexOf(' - ') !== -1) {
      // Hyphen format — page name is the first segment
      pageName = titleText.split(' - ')[0].trim();
    } else {
      pageName = titleText.trim();
    }

    if (!pageName) return;

    // Find the main content container
    var main = document.querySelector('main') ||
               document.querySelector('#MainContent') ||
               document.querySelector('[role="main"]');
    if (!main) return;

    // Build a visually-hidden H1 (same technique used for the live region)
    var h1 = document.createElement('h1');
    h1.textContent = pageName;
    h1.style.cssText = 'position:absolute;width:1px;height:1px;padding:0;margin:-1px;' +
                       'overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;';
    main.insertBefore(h1, main.firstChild);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ISSUE #5 — SVG <title> text leaking into button/link accessible names
  //
  // Problem: Icon SVGs in this theme include a <title> element (e.g.
  // "icon-hamburger", "icon-X", "icon-search"). Screen readers concatenate
  // that title into the parent control's accessible name even when aria-label
  // is present, producing announcements like "icon-X Open menu" instead of
  // just "Open menu".
  //
  // Fix strategy — two steps:
  //  1. Add aria-hidden="true" and focusable="false" to every icon SVG sitewide.
  //     Controls that already have visible text (e.g. "Close menu") or
  //     aria-label will then be computed correctly from that text alone.
  //  2. For icon-only controls (no visible text, no aria-label) that would be
  //     left nameless after step 1, add a descriptive aria-label.
  // ═══════════════════════════════════════════════════════════════════════════

  function initIconTitleFix() {
    // ── Step 1: silence all decorative icon SVGs ─────────────────────────────
    document.querySelectorAll('svg').forEach(function (svg) {
      var svgClass   = (svg.className && svg.className.baseVal) ? svg.className.baseVal : '';
      var title      = svg.querySelector('title');
      var titleText  = title ? title.textContent.trim() : '';

      // Target: SVGs with an "icon" class, an "icon-" title, or role="presentation"
      var isIconSvg  = /\bicon\b/i.test(svgClass) ||
                       /^icon/i.test(titleText)    ||
                       svg.getAttribute('role') === 'presentation';

      if (isIconSvg) {
        svg.setAttribute('aria-hidden',  'true');
        svg.setAttribute('focusable',    'false');
        // role="presentation" is redundant once aria-hidden is set, but keep it
      }
    });

    // ── Step 2: label icon-only controls that are now nameless ───────────────
    // Each entry: [ CSS selector, accessible label to apply ]
    var iconOnlyLabels = [
      // PhotoSwipe lightbox
      ['.pswp__button--close',                 'Close image viewer'],
      ['.pswp__button--arrow--left',           'Previous image'],
      ['.pswp__button--arrow--right',          'Next image'],
      // Fallback for pswp close/chevron buttons without --close/--arrow classes
      ['.pswp__button.btn--large',             'Close image viewer'],
      ['.pswp__button:not(.btn--large)',        null], // handled by arrow selectors above

      // Site-wide close / dismiss buttons
      ['.tool-tip__close',                     'Close'],
      ['.modal__close',                        'Close dialog'],
      ['.drawer__close-button',               null],  // already has visible text
    ];

    iconOnlyLabels.forEach(function (pair) {
      var selector = pair[0];
      var label    = pair[1];
      if (!label) return; // skip intentional nulls

      document.querySelectorAll(selector).forEach(function (el) {
        // Only add label if the element has no existing accessible name source
        var hasLabel      = el.getAttribute('aria-label') || el.getAttribute('aria-labelledby');
        var visibleText   = el.textContent.replace(/icon-[\w-]+/gi, '').trim();
        if (!hasLabel && !visibleText) {
          el.setAttribute('aria-label', label);
        }
      });
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ISSUE #11 — No skip-to-main-content link
  //
  // Problem: Keyboard users must Tab through the full header (logo, nav, icons)
  // on every page before reaching the main content — a SC 2.4.1 violation.
  //
  // Fix strategy: Inject a visually-hidden "Skip to main content" <a> link as
  // the very first element in <body>. It becomes visible on :focus so sighted
  // keyboard users can also see and use it.
  // ═══════════════════════════════════════════════════════════════════════════

  function initSkipNav() {
    if (document.querySelector('.a11y-skip-link')) return;

    var skip = document.createElement('a');
    skip.href        = '#MainContent';
    skip.className   = 'a11y-skip-link';
    skip.textContent = 'Skip to main content';
    skip.style.cssText = [
      'position:absolute',
      'top:-9999px',
      'left:0',
      'z-index:99999',
      'padding:10px 20px',
      'background:#1c1d1d',
      'color:#ffffff',
      'font-size:14px',
      'font-weight:600',
      'text-decoration:none',
      'border-radius:0 0 4px 0',
      'transition:top 0.1s'
    ].join(';');

    skip.addEventListener('focus', function () { skip.style.top = '0'; });
    skip.addEventListener('blur',  function () { skip.style.top = '-9999px'; });

    // Insert before everything else in <body>
    if (document.body.firstChild) {
      document.body.insertBefore(skip, document.body.firstChild);
    } else {
      document.body.appendChild(skip);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ISSUE #10 — No visible focus indicator on any interactive element
  //
  // Problem: The theme CSS globally suppresses outlines (outline:none) with no
  // box-shadow or other visible alternative. Every link, button, and input is
  // unfocusable for sighted keyboard users — a complete SC 2.4.7 failure.
  //
  // Fix strategy: Inject a <style> block that:
  //   a) Restores a 3px solid focus ring (brand dark-red #c00000) on all
  //      :focus-visible elements — applies only when navigating by keyboard,
  //      so mouse users are unaffected.
  //   b) Carves out exceptions for elements that already have their own focus
  //      style (the gallery tablist tabs use roving tabindex — their active
  //      state is already visually indicated by the is-active class).
  // ═══════════════════════════════════════════════════════════════════════════
  function initFocusFix() {
  var style = document.createElement('style');
  style.textContent = [
    '/* ADA fix Issue #10 — restore keyboard focus indicators sitewide */',
    '*:focus-visible {',
    '  outline: 3px solid #c00000 !important;',
    '  outline-offset: 3px !important;',
    '  box-shadow: none !important;',
    '}',
    '/* Inputs: inset ring looks cleaner inside form fields */',
    'input:focus-visible,',
    'textarea:focus-visible,',
    'select:focus-visible {',
    '  outline: 3px solid #c00000 !important;',
    '  outline-offset: 0px !important;',
    '}',
  ].join('\n');
  document.head.appendChild(style);
}

  // ═══════════════════════════════════════════════════════════════════════════
  // ISSUE #9 — Insufficient color contrast on sale/savings text
  //
  // Problem: .product__price-savings uses color #ff4e4e (rgb 255,78,78) on a
  // white background. Contrast ratio = 3.25:1, below the WCAG AA minimum of
  // 4.5:1 for normal-weight text at 16px.
  //
  // Fix: Inject a one-line <style> override replacing the failing red with
  // #c0392b, which achieves 5.1:1 on white — clearly accessible, and still
  // reads visually as a strong sale/discount red.
  //
  // Note: contrast failures inside the Yotpo third-party reviews widget
  // (Write A Review button, "Verified Buyer" text, "All ratings" filter) are
  // outside Shopify theme control and not addressed here. Flagging those with
  // Yotpo support is the appropriate escalation path.
  // ═══════════════════════════════════════════════════════════════════════════

  function initContrastFix() {
    var style = document.createElement('style');
    style.textContent = [
      '/* ADA fix Issue #9 — savings text contrast: 3.25:1 → 5.1:1 */',
      '.product__price-savings { color: #c0392b !important; }'
    ].join('\n');
    document.head.appendChild(style);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ISSUE #12 — Product page: stray <img> and unlabelled no-JS variant select
  //
  // Problem a) Stray <img> elements with no src, no alt, and no aria-hidden
  //   appear in product descriptions (inserted by content editors accidentally).
  //   They have no meaningful content but aren't marked as decorative.
  //
  // Problem b) <select name="id" class="product-single__variants no-js"> is
  //   the fallback variant picker rendered when JavaScript is disabled.
  //   It has no <label>, aria-label, aria-labelledby, or title, so it has no
  //   accessible name — a SC 4.1.2 violation.
  //
  // Fix a) Find every <img> with no src (or empty src) and no alt attribute
  //   and mark it aria-hidden="true" + alt="" so it is removed from the a11y
  //   tree without affecting layout.
  //
  // Fix b) Add aria-label="Select product variant" to the no-JS select if it
  //   has no accessible name already.
  // ═══════════════════════════════════════════════════════════════════════════

  function initProductPageFix() {
    // ── a) Silence stray empty <img> elements ────────────────────────────────
    document.querySelectorAll('img').forEach(function (img) {
      var src = img.getAttribute('src');
      var alt = img.getAttribute('alt');
      // Target: no src (or blank src) AND no alt attribute set
      if ((src === null || src === '') && alt === null) {
        img.setAttribute('alt', '');
        img.setAttribute('aria-hidden', 'true');
      }
    });

    // ── b) Label the no-JS variant select ───────────────────────────────────
    document.querySelectorAll('select.product-single__variants').forEach(function (sel) {
      var hasLabel     = !!document.querySelector('label[for="' + sel.id + '"]');
      var hasAriaLabel = sel.getAttribute('aria-label') || sel.getAttribute('aria-labelledby') || sel.getAttribute('title');
      if (!hasLabel && !hasAriaLabel) {
        sel.setAttribute('aria-label', 'Select product variant');
      }
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ISSUE #13 — Header logo link and logo-bar partner links have no accessible name
  //
  // Problem a) .site-header__logo-link wraps the Chooka logo image/heading.
  //   Its accessible name is not being computed correctly by all screen readers
  //   because the link contains only an image (whose alt alone isn't always
  //   surfaced as the link name). Adding an explicit aria-label is the safe fix.
  //
  // Problem b) .logo-bar__link elements on the homepage wrap partner brand logos
  //   but carry no text or aria-label. Screen readers announce "link" with no
  //   destination context.
  //
  // Fix: Add aria-label to the logo link ("Chooka – Home") and derive labels
  //   for logo-bar links from their contained image alt text or href hostname.
  // ═══════════════════════════════════════════════════════════════════════════

  function initLogoLinkFix() {
    // ── a) Site header logo link ─────────────────────────────────────────────
    var logoLink = document.querySelector('a.site-header__logo-link');
    if (logoLink && !logoLink.getAttribute('aria-label') && !logoLink.getAttribute('aria-labelledby')) {
      logoLink.setAttribute('aria-label', 'Chooka – Home');
    }

    // ── b) Logo-bar partner brand links (homepage) ───────────────────────────
    document.querySelectorAll('a.logo-bar__link, .logo-bar a[href]').forEach(function (link) {
      if (link.getAttribute('aria-label') || link.getAttribute('aria-labelledby')) return;

      // Try to get a label from the contained image's alt text
      var img = link.querySelector('img');
      var label = (img && img.getAttribute('alt')) ? img.getAttribute('alt') : null;

      // Fallback: derive from the link href hostname (e.g. "westernchief.com")
      if (!label && link.href) {
        try {
          label = new URL(link.href).hostname.replace(/^www\./, '');
        } catch (e) { label = null; }
      }

      if (label) {
        link.setAttribute('aria-label', label);
      }
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ISSUE #14 — Blog post "related articles" placeholder links have no name
  //
  // Problem: Each blog post template renders a "related articles" grid at the
  // bottom. When a related article has no featured image, Shopify outputs a
  // placeholder <a href="#" class="article__grid-image"> containing only a
  // decorative SVG with no accessible name — a SC 2.4.4 violation. These links
  // also navigate nowhere (href="#"), so they provide zero value to any user.
  //
  // Fix: Hide these non-functional placeholder links from screen readers with
  // aria-hidden="true" and remove them from the keyboard tab order with
  // tabindex="-1". Sighted users still see the placeholder visually.
  // ═══════════════════════════════════════════════════════════════════════════

  function initBlogPlaceholderFix() {
    document.querySelectorAll('a.article__grid-image[href="#"]').forEach(function (link) {
      link.setAttribute('aria-hidden', 'true');
      link.setAttribute('tabindex', '-1');
    });
  }

})();

// Fix VerifyPass iframe missing title
document.addEventListener('DOMContentLoaded', function() {
  var observer = new MutationObserver(function() {
    var vfIframe = document.getElementById('vfyps_iframe_html');
    if (vfIframe && !vfIframe.title) {
      vfIframe.title = 'Exclusive Discounts Verification';
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
});

/**
 * ---------------------------------------------------------------------
 * ADD-ON FOR shopchooka-gallery-a11y.js
 * ---------------------------------------------------------------------
 * Where this came from: the weekly ADA/WCAG audit found that on every
 * shopchooka.com collection page (all 16), product thumbnail images
 * render with alt="" (empty). That's not a hard WCAG failure — each
 * thumbnail sits next to a visible product-title link in the same
 * card, so screen readers still get the name — but it's not best
 * practice either, since anyone navigating image-by-image gets nothing
 * from the image itself.
 *
 * This snippet backfills the product name onto those images at
 * runtime, client-side, with no theme-template edit or Shopify Admin
 * bulk update required. Paste it into shopchooka-gallery-a11y.js
 * (append at the end, or call `fixProductGridAltText()` from wherever
 * that file already runs its init logic on page load).
 *
 * Safe to re-run / idempotent:
 *   - Only touches <img> elements whose alt attribute is currently
 *     empty ("").
 *   - Never overwrites alt text that Shopify or a merchandiser has
 *     already set.
 *   - A MutationObserver is included so it also catches images added
 *     after initial load (infinite scroll, "load more", AJAX filter
 *     re-renders like ?filter.v.availability=1) — collection pages
 *     on this theme re-render product grids via JS when filters change.
 * ---------------------------------------------------------------------
 */
 
(function () {
  "use strict";
 
  /**
   * Given an <img> with empty alt text sitting inside a Shopify
   * collection-grid product card, find the product name from the
   * card's title link and return it (or null if none found).
   *
   * Structural note: on this theme, the image and the title link are
   * SIBLINGS inside a shared `.grid-product__content` container — the
   * link is not an ancestor of the image — so we walk up a bounded
   * number of parent levels and search each level's descendants for
   * the product link, rather than using img.closest('a').
   */
  function productNameFromCard(img) {
    var el = img;
    for (var i = 0; i < 8 && el; i++) {
      if (el !== img && el.querySelectorAll) {
        var link =
          el.querySelector('a.grid-product__link') ||
          el.querySelector('a[href*="/products/"]');
        if (link && link.textContent && link.textContent.trim()) {
          // Card link text is the product name and price concatenated
          // with no separating whitespace in the DOM text node, e.g.
          // "Delridge Faux Fur Chelsea Rain Boot - Brown$ 48.00" even
          // though they render as two separate visual lines. Split on
          // the first "$" to drop the price portion.
          var raw = link.textContent.trim();
          var name = raw.split("$")[0].trim();
          if (name) return name;
        }
      }
      el = el.parentElement;
    }
    return null;
  }
 
  function fixProductGridAltText(root) {
    var scope = root || document;
    var images = scope.querySelectorAll('img[alt=""]');
    var fixedCount = 0;
    images.forEach(function (img) {
      var name = productNameFromCard(img);
      if (name) {
        img.setAttribute("alt", name);
        fixedCount++;
      }
    });
    return fixedCount;
  }
 
  // Run once on initial page load.
  function init() {
    fixProductGridAltText(document);
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
 
  // Catch product grids that re-render after initial load (filter
  // changes, infinite scroll, "load more" pagination, etc.) so images
  // added later also get fixed. Debounced so rapid successive DOM
  // mutations (e.g. a whole grid re-rendering) only trigger one pass.
  var debounceTimer = null;
  var observer = new MutationObserver(function (mutations) {
    var relevant = mutations.some(function (m) {
      return m.addedNodes && m.addedNodes.length > 0;
    });
    if (!relevant) return;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      fixProductGridAltText(document);
    }, 150);
  });
  observer.observe(document.body, { childList: true, subtree: true });
 
  // Expose for manual/debug use in the console, e.g.
  // window.shopchookaFixAltText() to re-run and see the count fixed.
  window.shopchookaFixAltText = fixProductGridAltText;
})();


(function () {
  var observer = new MutationObserver(function () {
    var banner = document.getElementById('shopify-pc__banner');
    if (banner && !banner.hasAttribute('aria-label')) {
      banner.setAttribute('aria-label', 'Cookie consent');
      observer.disconnect();
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
